<template>
    <div id="messenger-ad-optimization">
        <div class="ad-optimization-container">
            <div class="section">
                <div class="section-header">
                    <h3>CAPI Durumu</h3>
                    <a-switch
                        :checked="capiEnabled"
                        @change="toggleCapi"
                        checked-children="Aktif"
                        un-checked-children="Pasif"
                    />
                </div>

                <div v-if="capiEnabled" class="section-body">
                    <!-- Bağlantı Yöntemi -->
                    <div class="form-group">
                        <label class="main-label">Bağlantı Yöntemi</label>
                        <a-radio-group
                            v-model="connectionMethod"
                            @change="handleConnectionMethodChange"
                            class="radio-group"
                        >
                            <a-radio value="oauth" class="radio-item">
                                <span class="radio-text">Connect through Meta</span>
                            </a-radio>
                            <a-radio value="manual" class="radio-item">
                                <span class="radio-text">With Dataset and Token</span>
                            </a-radio>
                        </a-radio-group>
                    </div>

                    <!-- OAuth Yöntemi -->
                    <div v-if="connectionMethod === 'oauth'" class="oauth-section">
                        <div class="info-box">
                            <i class="material-icons">info</i>
                            <p>
                                Meta üzerinden bağlantı kurmak için tekrar yetkilendirme yapmanız gerekiyor. Bu işlem
                                mevcut kanalınızı bozmaz, sadece ek izinler ekler.
                            </p>
                        </div>

                        <SupsisButton primary :disabled="oauthLoading" @click="startOauth" icon="link">
                            {{ oauthLoading ? "Bağlanıyor..." : "Meta ile Bağlan" }}
                        </SupsisButton>

                        <!-- Dataset Seçimi -->
                        <div v-if="datasets.length > 0" class="dataset-section">
                            <label class="main-label">Dataset Seçimi</label>
                            <a-select
                                v-model="selectedDatasetId"
                                placeholder="Bir dataset seçin"
                                style="width: 100%"
                                @change="handleDatasetChange"
                            >
                                <a-select-option v-for="dataset in datasets" :key="dataset.id" :value="dataset.id">
                                    {{ dataset.name }}
                                </a-select-option>
                            </a-select>

                            <SupsisButton :disabled="creatingDataset" @click="createNewDataset" icon="add" class="mt-2">
                                {{ creatingDataset ? "Oluşturuluyor..." : "Yeni Dataset Oluştur" }}
                            </SupsisButton>
                        </div>
                    </div>

                    <!-- Manuel Yöntem -->
                    <div v-if="connectionMethod === 'manual'" class="manual-section">
                        <div class="form-group">
                            <label class="main-label">Dataset ID</label>
                            <a-input
                                v-model="manualDatasetId"
                                placeholder="Dataset ID'sini girin"
                                @change="handleManualDatasetChange"
                            />
                        </div>

                        <div class="form-group">
                            <label class="main-label">Access Token</label>
                            <a-input
                                v-model="manualAccessToken"
                                type="password"
                                placeholder="Access token'ı girin"
                                @change="handleManualTokenChange"
                            />
                        </div>
                    </div>

                    <!-- Test Bağlantı -->
                    <div v-if="hasDatasetAndToken" class="test-section">
                        <SupsisButton :disabled="testing" @click="testConnection" icon="check_circle">
                            {{ testing ? "Test Ediliyor..." : "Bağlantıyı Test Et" }}
                        </SupsisButton>
                        <span v-if="testResult !== null" class="test-result" :class="testResult ? 'success' : 'error'">
                            <i class="material-icons">{{ testResult ? "check_circle" : "cancel" }}</i>
                            {{ testResult ? "Bağlantı başarılı" : "Bağlantı başarısız" }}
                        </span>
                    </div>

                    <!-- Otomatik Event Ayarları -->
                    <div v-if="hasDatasetAndToken" class="auto-events-section">
                        <h4>Otomatik Event Gönderimi</h4>
                        <div class="event-toggles">
                            <div class="event-toggle" v-for="event in eventTypes" :key="event.key">
                                <a-switch
                                    :checked="autoEvents[event.key]"
                                    @change="(val) => toggleAutoEvent(event.key, val)"
                                    size="small"
                                />
                                <div class="event-info">
                                    <span class="event-label">{{ event.label }}</span>
                                    <span class="event-desc">{{ event.description }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import CONFIG from "../../../../../config/config";
import { CAPI_API } from "../../../../../api/capi";
import { socketMessageStream } from "../../../../../utils/socketStream";
import { first, map, timeout } from "rxjs/operators";
import { generateRandomId } from "../../../../../utils/session";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

export default {
    name: "MessengerAdOptimization",
    components: {
        SupsisButton,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            channel: _.cloneDeep(this.value),
            capiEnabled: _.get(this.value, "capi.enabled", false),
            connectionMethod: _.get(this.value, "capi.connectionMethod", "oauth"),
            selectedDatasetId: _.get(this.value, "capi.datasetId", null),
            manualDatasetId: _.get(this.value, "capi.datasetId", ""),
            manualAccessToken: _.get(this.value, "capi.accessToken", ""),
            autoEvents: _.get(this.value, "capi.autoEvents", {}),
            datasets: [],
            oauthLoading: false,
            creatingDataset: false,
            testing: false,
            testResult: null,
            eventTypes: [
                {
                    key: "LeadSubmitted",
                    label: "Lead Gönderildi",
                    description: "Meta lead formu doldurulduğunda",
                },
                {
                    key: "InitiateCheckout",
                    label: "Sohbet Başlatıldı",
                    description: "Yeni konuşma oluşturulduğunda",
                },
                {
                    key: "Purchase",
                    label: "Satın Alma",
                    description: "Satın alma tamamlandığında",
                },
                {
                    key: "AppointmentBooked",
                    label: "Randevu Oluşturuldu",
                    description: "Randevu rezervasyonu yapıldığında",
                },
                {
                    key: "AddToCart",
                    label: "Sepete Eklendi",
                    description: "Ürün sepete eklendiğinde",
                },
                {
                    key: "ViewContent",
                    label: "İçerik Görüntülendi",
                    description: "Bir sayfa veya ürün görüntülendiğinde",
                },
                {
                    key: "OrderCreated",
                    label: "Sipariş Oluşturuldu",
                    description: "Sipariş başarıyla oluşturulduğunda",
                },
                {
                    key: "OrderShipped",
                    label: "Sipariş Kargoya Verildi",
                    description: "Sipariş kargoya verildiğinde",
                },
            ],
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
            userId: (state) => state.auth.user?._id || state.auth.adminUser?._id,
        }),
        hasDatasetAndToken() {
            if (this.connectionMethod === "oauth") {
                return !!this.selectedDatasetId;
            } else {
                return !!this.manualDatasetId && !!this.manualAccessToken;
            }
        },
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        toggleCapi(enabled) {
            this.capiEnabled = enabled;
            this.saveChanges();
        },
        handleConnectionMethodChange() {
            this.testResult = null;
            this.saveChanges();
        },
        handleDatasetChange(datasetId) {
            this.selectedDatasetId = datasetId;
            this.testResult = null;
            this.saveChanges();
        },
        handleManualDatasetChange() {
            this.testResult = null;
            this.saveChanges();
        },
        handleManualTokenChange() {
            this.testResult = null;
            this.saveChanges();
        },
        toggleAutoEvent(eventKey, enabled) {
            this.autoEvents[eventKey] = enabled;
            this.saveChanges();
        },
        async startOauth() {
            const FACEBOOK_APP_ID = "7174288719300175";
            this.oauthLoading = true;
            const redirectUrl = `${CONFIG.SOCKET_URL}/webhook/oauth2/facebook`;

            const scopes = [
                "public_profile",
                "instagram_basic",
                "instagram_manage_messages",
                "instagram_manage_comments",
                "instagram_manage_events",
                "pages_manage_metadata",
                "pages_manage_engagement",
                "pages_show_list",
                "pages_read_engagement",
                "pages_messaging",
                "business_management",
                "pages_read_user_content",
                "pages_manage_ads",
                "ads_management",
            ];

            const scope = scopes.join(",");
            const state = this.siteId + "_" + this.userId + "_messenger-capi_" + generateRandomId(4);
            const url = `https://www.facebook.com/v19.0/dialog/oauth?client_id=${FACEBOOK_APP_ID}&redirect_uri=${redirectUrl}&state=${state}&scope=${scope}&auth_type=rerequest`;

            this.listenOauthCallback();
            this.oauthWindow = window.open(url, "_blank");
        },
        listenOauthCallback() {
            socketMessageStream
                .pipe(
                    first((x) => x.cmd === "facebook_callback_response"),
                    map((x) => x.data),
                    timeout(60000 * 10)
                )
                .toPromise()
                .then(async (data) => {
                    this.oauthLoading = false;
                    this.oauthWindow.close();
                    this.$message.success("Meta bağlantısı başarılı");

                    // Token'ı güncelle
                    const accessToken = _.get(data, "tokenInfo.accessToken.access_token");
                    if (accessToken) {
                        this.channel.payload.oauth = data.tokenInfo;
                        this.channel.payload.page.access_token = accessToken;
                    }

                    // Dataset'leri listele
                    await this.loadDatasets();
                })
                .catch((err) => {
                    this.oauthLoading = false;
                    this.oauthWindow.close();
                    this.$message.error("Meta bağlantısı başarısız");
                });
        },
        async loadDatasets() {
            try {
                const response = await CAPI_API.listDatasets(this.channel._id);
                this.datasets = response.data || response || [];

                if (this.datasets.length > 0 && !this.selectedDatasetId) {
                    this.selectedDatasetId = this.datasets[0].id;
                    this.saveChanges();
                }
            } catch (error) {
                console.error("Dataset listeleme hatası:", error);
                this.$message.error("Dataset'ler yüklenemedi");
            }
        },
        async createNewDataset() {
            this.creatingDataset = true;
            try {
                const response = await CAPI_API.createDataset(this.channel._id);
                const datasetId = response.data?.datasetId || response.datasetId;

                this.$message.success("Yeni dataset oluşturuldu");
                this.selectedDatasetId = datasetId;

                // Dataset listesini yenile
                await this.loadDatasets();
                this.saveChanges();
            } catch (error) {
                console.error("Dataset oluşturma hatası:", error);
                this.$message.error("Dataset oluşturulamadı");
            } finally {
                this.creatingDataset = false;
            }
        },
        async testConnection() {
            this.testing = true;
            this.testResult = null;

            try {
                const response = await CAPI_API.testConnection(this.channel._id);
                this.testResult = response.data?.success || response.success || false;

                if (this.testResult) {
                    this.$message.success("CAPI bağlantısı başarılı!");
                } else {
                    this.$message.error("CAPI bağlantısı başarısız");
                }
            } catch (error) {
                console.error("CAPI test hatası:", error);
                this.testResult = false;
                this.$message.error("Bağlantı testi yapılamadı");
            } finally {
                this.testing = false;
            }
        },
        saveChanges() {
            // CAPI ayarlarını kanalda güncelle
            if (!this.channel.capi) {
                this.channel.capi = {};
            }

            this.channel.capi.enabled = this.capiEnabled;
            this.channel.capi.connectionMethod = this.connectionMethod;
            this.channel.capi.autoEvents = this.autoEvents;

            if (this.connectionMethod === "oauth") {
                this.channel.capi.datasetId = this.selectedDatasetId;
                this.channel.capi.accessToken = null;
            } else {
                this.channel.capi.datasetId = this.manualDatasetId;
                this.channel.capi.accessToken = this.manualAccessToken;
            }

            this.$emit("input", this.channel);
        },
    },
    mounted() {
        // OAuth yöntemi seçiliyse ve token varsa dataset'leri yükle
        if (this.connectionMethod === "oauth" && this.capiEnabled) {
            this.loadDatasets();
        }
    },
};
</script>

<style lang="scss" scoped>
#messenger-ad-optimization {
    padding: 20px;
    background: var(--color-bg-default);

    .ad-optimization-container {
        max-width: 800px;

        .section {
            background: white;
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

            .section-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 24px;
                padding-bottom: 16px;
                border-bottom: 2px solid var(--color-border-default);

                h3 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: var(--color-text-default);
                }
            }

            .section-body {
                display: flex;
                flex-direction: column;
                gap: 24px;

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .main-label {
                        font-weight: 600;
                        color: var(--color-text-default);
                        font-size: 15px;
                        margin-bottom: 4px;
                    }

                    .radio-group {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                        padding: 8px 0;

                        .radio-item {
                            display: flex;
                            align-items: center;
                            padding: 8px 0;

                            .radio-text {
                                margin-left: 8px;
                                font-size: 14px;
                                color: var(--color-text-default);
                            }
                        }
                    }
                }

                .info-box {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 16px;
                    background: #e3f2fd;
                    border-radius: 8px;
                    border-left: 4px solid #2196f3;

                    i {
                        color: #2196f3;
                        font-size: 22px;
                        flex-shrink: 0;
                    }

                    p {
                        margin: 0;
                        font-size: 14px;
                        color: #1565c0;
                        line-height: 1.6;
                    }
                }

                .oauth-section,
                .manual-section {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;

                    .dataset-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                        margin-top: 8px;
                        padding: 16px;
                        background: var(--color-bg-subtle);
                        border-radius: 8px;
                    }
                }

                .test-section {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    padding: 18px;
                    background: var(--color-bg-subtle);
                    border-radius: 8px;

                    .test-result {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-weight: 500;
                        font-size: 14px;

                        i {
                            font-size: 18px;
                        }

                        &.success {
                            color: #52c41a;
                        }

                        &.error {
                            color: #ff4d4f;
                        }
                    }
                }

                .auto-events-section {
                    padding: 20px;
                    background: var(--color-bg-subtle);
                    border-radius: 8px;

                    h4 {
                        margin: 0 0 16px 0;
                        font-size: 16px;
                        font-weight: 600;
                        color: var(--color-text-default);
                    }

                    .event-toggles {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;

                        .event-toggle {
                            display: flex;
                            align-items: center;
                            gap: 12px;
                            padding: 12px;
                            border-radius: 6px;
                            transition: all 0.2s;

                            &:hover {
                                background: rgba(0, 0, 0, 0.03);
                            }

                            .event-info {
                                display: flex;
                                flex-direction: column;
                                gap: 4px;
                                flex: 1;

                                .event-label {
                                    font-weight: 500;
                                    color: var(--color-text-default);
                                    font-size: 14px;
                                }

                                .event-desc {
                                    font-size: 13px;
                                    color: var(--color-text-subtle);
                                    line-height: 1.4;
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    .mt-2 {
        margin-top: 8px;
    }
}
</style>
